import React from 'react';
import { BarChart3, TrendingUp, Calendar, Grid, RefreshCw } from 'lucide-react';
import Chart from './Chart';
import MonthlyTrendsRenderer from './MonthlyTrendsRenderer';
import Heatmap from './Heatmap';

const TrendsDashboard = ({
    charts,
    fetchChartData,
    monthlyTrends,
    fetchMonthlyTrends,
    heatmapData,
    fetchHeatmapData,
    loadingStates = {},
    apiClient
}) => {
    const renderChart = (chartData, loadingKey, chartTitle = 'Chart') => {
        // Show loading state if chart is loading
        if (loadingStates[loadingKey]) {
            return (
                <div className="flex items-center justify-center p-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading {chartTitle.toLowerCase()}...</p>
                        <p className="text-sm text-gray-500 mt-2">Please wait</p>
                    </div>
                </div>
            );
        }

        if (!chartData) {
            return (
                <div className="flex items-center justify-center h-64 text-gray-400">
                    <div className="text-center">
                        <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Chart not loaded yet</p>
                        <p className="text-xs text-gray-500 mt-2">Click Refresh Chart to load data</p>
                    </div>
                </div>
            );
        }

        if (chartData.error) {
            return (
                <div className="flex items-center justify-center h-64 text-gray-400">
                    <div className="text-center">
                        <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Chart data not available</p>
                        <p className="text-xs text-gray-500 mt-2">Try refreshing the chart</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="p-4 sm:p-6 overflow-hidden">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">{chartData.options?.title?.text || 'Chart'}</h3>
                <div className="overflow-x-auto min-h-[280px]">
                    <div className="w-full min-w-[280px]" style={{ minHeight: 280 }}>
                        <Chart
                            data={chartData.data}
                            type={chartData.type}
                            options={chartData.options}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-8 animate-fade-in-up">
            {/* Header / Intro */}
            <div className="rounded-2xl p-6 shadow-sm border" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                <div className="flex items-center">
                    <div className="p-3 bg-blue-50 rounded-xl mr-4">
                        <TrendingUp className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Trends & Analysis</h2>
                        <p className="text-gray-500 mt-1">Deep dive into fleet performance, unplanned events, and historical patterns.</p>
                    </div>
                </div>
            </div>

            {/* Original Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Unit Unplanned Events Chart */}
                <div className="rounded-2xl shadow-sm border overflow-hidden hover:shadow-md transition-all duration-300 group" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                    <div className="p-6 border-b backdrop-blur-sm flex justify-between items-start" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 flex items-center mb-1">
                                <div className="p-2 bg-red-50 rounded-lg mr-2 group-hover:bg-red-100 transition-colors">
                                    <BarChart3 className="h-5 w-5 text-red-500" />
                                </div>
                                Unit Unplanned Events
                            </h3>
                            <p className="text-sm text-gray-500 ml-11">Focus on unplanned maintenance events</p>
                        </div>
                        <button
                            onClick={() => fetchChartData('unit_issues')}
                            className="text-gray-400 hover:text-red-500 p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Refresh Chart"
                        >
                            <RefreshCw className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                    </div>

                    {renderChart(charts.unit_issues, 'chart_unit_issues', 'Unit Unplanned Events Chart')}

                    <div className="px-6 py-3 border-t flex justify-end" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                        <button
                            onClick={() => fetchChartData('unit_issues')}
                            className="text-xs font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-md transition-colors uppercase tracking-wide flex items-center"
                        >
                            Refresh Chart
                        </button>
                    </div>
                </div>

                {/* Monthly Unplanned Events Trend */}
                <div className="rounded-2xl shadow-sm border overflow-hidden hover:shadow-md transition-all duration-300 group" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                    <div className="p-6 border-b backdrop-blur-sm flex justify-between items-start" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 flex items-center mb-1">
                                <div className="p-2 bg-purple-50 rounded-lg mr-2 group-hover:bg-purple-100 transition-colors">
                                    <TrendingUp className="h-5 w-5 text-purple-500" />
                                </div>
                                Monthly Unplanned Event Trend
                            </h3>
                            <p className="text-sm text-gray-500 ml-11">Track unplanned maintenance event trends</p>
                        </div>
                        <button
                            onClick={() => fetchChartData('monthly_trend')}
                            className="text-gray-400 hover:text-purple-500 p-2 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Refresh Chart"
                        >
                            <RefreshCw className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${loadingStates.chart_monthly_trend ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                    {renderChart(charts.monthly_trend, 'chart_monthly_trend', 'Monthly Unplanned Events Trend')}
                    <div className="px-6 py-3 border-t flex justify-end" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                        <button
                            onClick={() => fetchChartData('monthly_trend')}
                            className="text-xs font-semibold text-purple-600 hover:text-purple-700 hover:bg-purple-50 px-3 py-1.5 rounded-md transition-colors uppercase tracking-wide"
                        >
                            Refresh Chart
                        </button>
                    </div>
                </div>
            </div>

            {/* New Features: Monthly Trends Analysis */}
            <div className="rounded-2xl shadow-sm border overflow-hidden hover:shadow-md transition-all duration-300 group" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                <div className="p-6 border-b backdrop-blur-sm flex justify-between items-start" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 flex items-center mb-1">
                            <div className="p-2 bg-blue-50 rounded-lg mr-2 group-hover:bg-blue-100 transition-colors">
                                <Calendar className="h-5 w-5 text-blue-500" />
                            </div>
                            Monthly Unplanned Maintenance Trends
                        </h3>
                        <p className="text-sm text-gray-500 ml-11">Analyze unplanned maintenance patterns by month and unit</p>
                    </div>
                    {fetchMonthlyTrends && (
                        <button
                            onClick={() => fetchMonthlyTrends(true)}
                            disabled={loadingStates.monthlyTrends}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Refresh monthly trends (bypass cache)"
                        >
                            <RefreshCw className={`w-4 h-4 ${loadingStates.monthlyTrends ? 'animate-spin' : ''}`} />
                        </button>
                    )}
                </div>
                <div className="p-6">
                    <MonthlyTrendsRenderer
                        monthlyTrends={monthlyTrends}
                        loading={loadingStates.monthlyTrends}
                    />
                </div>
            </div>

            {/* Heatmap Analysis */}
            <div className="rounded-2xl shadow-sm border overflow-hidden hover:shadow-md transition-all duration-300 group" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                <div className="p-6 border-b backdrop-blur-sm" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center mb-1">
                        <div className="p-2 bg-orange-50 rounded-lg mr-2 group-hover:bg-orange-100 transition-colors">
                            <Grid className="h-5 w-5 text-orange-500" />
                        </div>
                        Events vs Units Heatmap
                    </h3>
                    <p className="text-sm text-gray-500 ml-11">Visualize event patterns across units to identify hotspots</p>
                </div>
                <div className="p-6">
                    <Heatmap
                        heatmapData={heatmapData}
                        loading={loadingStates.heatmap}
                        apiClient={apiClient}
                    />
                </div>
                <div className="px-6 py-3 border-t flex justify-end" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--card-border)' }}>
                    <button
                        onClick={() => fetchHeatmapData && fetchHeatmapData('unplanned')}
                        className="text-xs font-semibold text-orange-600 hover:text-orange-700 hover:bg-orange-50 px-3 py-1.5 rounded-md transition-colors uppercase tracking-wide"
                    >
                        Refresh Heatmap
                    </button>
                </div>
            </div>

        </div>
    );
};

export default TrendsDashboard;
